import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, SyntheticEvent } from "react";

import { GANTT_TOOLTIP_ATTRIBUTE } from "@/shared/components/gantt";

import type { GanttTipCard } from "../gantt-rows";

const GAP_PX = 8;
const MARGIN_PX = 8;
const SHOW_DELAY_MS = 150;

const HIDDEN: CSSProperties = { top: 0, left: 0, visibility: "hidden" };

export type GanttTipAnchorProps =
  | {
      [GANTT_TOOLTIP_ATTRIBUTE]: true;
      "data-pr-tooltip": string;
    }
  | {
      "aria-describedby": string | undefined;
      onMouseEnter: (event: SyntheticEvent<HTMLElement>) => void;
      onMouseLeave: () => void;
      onFocus: (event: SyntheticEvent<HTMLElement>) => void;
      onBlur: () => void;
      onKeyDown: (event: { key: string }) => void;
    };

/** O que o `GanttTipCard` desenha: o dado, o id da âncora, a posição e a ref do elemento. */
export interface GanttTipState {
  readonly card: GanttTipCard;
  readonly id: string;
  readonly style: CSSProperties;
  readonly cardRef: (element: HTMLDivElement | null) => void;
}

/**
 * Tooltip das barras do Gantt. Com `card`, abre o card estruturado (em portal, `position: fixed`,
 * então o `overflow` do scroller não o corta) acima do elemento, após 150 ms, no hover/foco.
 * Sem `card`, cai no tooltip de texto do PrimeReact (`text`).
 *
 * Espalhe `anchorProps` no elemento (o que recebe o hover ou o foco é a âncora do card) e passe `tip` ao
 * `GanttTipCard` ao lado dele; o elemento ainda precisa de `tabIndex`.
 */
export function useGanttTip(text: string, card: GanttTipCard | undefined) {
  const anchor = useRef<HTMLElement | null>(null);
  const cardElement = useRef<HTMLDivElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<CSSProperties>(HIDDEN);
  const tooltipId = useId();

  const cardRef = useCallback((element: HTMLDivElement | null) => {
    cardElement.current = element;
  }, []);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const target = anchor.current;
    const element = cardElement.current;
    if (!target || !element) return;

    const rect = target.getBoundingClientRect();
    const { width, height } = element.getBoundingClientRect();
    const above = rect.top - GAP_PX - height;
    // Posição "top": abre acima; embaixo só quando não cabe em cima.
    const top = above >= MARGIN_PX ? above : rect.bottom + GAP_PX;
    const centered = rect.left + rect.width / 2 - width / 2;
    const left = Math.max(MARGIN_PX, Math.min(centered, window.innerWidth - width - MARGIN_PX));
    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  // Desmontar com o timer pendente não pode abrir o card nem deixar o timeout solto.
  useEffect(() => clearTimer, [clearTimer]);

  const show = useCallback(
    (event: SyntheticEvent<HTMLElement>) => {
      anchor.current = event.currentTarget;
      clearTimer();
      timer.current = setTimeout(() => {
        timer.current = null;
        setPosition(HIDDEN);
        setOpen(true);
      }, SHOW_DELAY_MS);
    },
    [clearTimer],
  );

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  if (!card) {
    const anchorProps: GanttTipAnchorProps = {
      [GANTT_TOOLTIP_ATTRIBUTE]: true,
      "data-pr-tooltip": text,
    };
    return { anchorProps, tip: null as GanttTipState | null };
  }

  const anchorProps: GanttTipAnchorProps = {
    "aria-describedby": open ? tooltipId : undefined,
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide,
    onKeyDown: (event) => {
      if (event.key === "Escape") hide();
    },
  };

  const tip: GanttTipState | null = open ? { card, id: tooltipId, style: position, cardRef } : null;

  return { anchorProps, tip };
}
